import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import lodash from 'lodash';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { fadeInOut } from '@helpers/fadeInOut';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    animations: [fadeInOut],
    standalone: false
})
export class CalendarComponent implements OnInit, OnDestroy {
    MIN_YEAR: number = 2024;
    MAX_YEAR: number = 2100;
    component: any;
    [x: string]: any;
    public currentDate: dayjs.Dayjs;
    public namesOfDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    public weeks: Array<CalendarDate[]> = [];

    selectedYear = undefined;
    selectedMonth = undefined;
    selectedDay = undefined;

    invalidYearError: string;
    invalidMonthError: string;
    invalidDayError: string;
    invalidDateError: string;

    public show: boolean;

    @Input() label: string = '';
    @Input() name: string = '';
    @Input() clickRefreshSubject: Subject<any>;

    @Output() onSelectDateEvent = new EventEmitter<any>();

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.show = false;
        }
    }
    constructor(
        private eRef: ElementRef,
        private translationService: TranslateService,
    ) {}

    ngOnInit() {
        this.currentDate = dayjs(new Date());

        this.generateCalendar();
        this.clickRefreshSubject.subscribe((e) => {
            if (e) {
                this.selectedYear = '';
                this.selectedMonth = '';
                this.selectedDay = '';
            }
        });
    }

    ngOnDestroy() {
        this.clickRefreshSubject.unsubscribe();
    }

    generateCalendar(): void {
        const dates = this.fillDates(this.currentDate);
        const weeks = [];
        while (dates.length > 0) {
            weeks.push(dates.splice(0, 7));
        }
        this.weeks = weeks;
    }

    fillDates(currentMoment: dayjs.Dayjs) {
        const firstOfMonth = dayjs(currentMoment).startOf('month').day();

        const lastOfMonth = dayjs(currentMoment).endOf('month').day();

        const firstDayOfGrid = dayjs(currentMoment).startOf('month').subtract(firstOfMonth, 'days');
        const lastDayOfGrid = dayjs(currentMoment)
            .endOf('month')
            .subtract(lastOfMonth, 'days')
            .add(7, 'days');
        const startCalendar = firstDayOfGrid.date();

        return lodash.range(startCalendar, startCalendar + lastDayOfGrid.diff(firstDayOfGrid, 'days')).map(
            (date) => {
                const newDate = dayjs(firstDayOfGrid).date(date);
                return {
                    today: this.isToday(newDate),
                    selected: this.isSelected(newDate),
                    mDate: newDate,
                };
            },
        );
    }

    isToday(date: dayjs.Dayjs): boolean {
        return dayjs().isSame(dayjs(date), 'day');
    }

    isSelected(date: dayjs.Dayjs): boolean {
        const selectedDate = this.selectedYear + '/' + this.selectedMonth + '/' + this.selectedDay;
        return selectedDate === dayjs(date).format('YYYY/MM/DD');
    }

    prevMonth(): void {
        const newDate = dayjs(this.currentDate).subtract(1, 'months');
        if (dayjs(newDate).get('year') >= this.MIN_YEAR) {
            this.currentDate = newDate;
            this.generateCalendar();
        }
    }

    prevYear(): void {
        const newDate = dayjs(this.currentDate).subtract(1, 'years');
        if (dayjs(newDate).get('year') > this.MIN_YEAR) {
            this.currentDate = newDate;
            this.generateCalendar();
        }
    }

    nextMonth(): void {
        const newDate = dayjs(this.currentDate).add(1, 'months');
        if (dayjs(newDate).get('year') < this.MAX_YEAR) {
            this.currentDate = newDate;
            this.generateCalendar();
        }
    }

    nextYear(): void {
        const newDate = dayjs(this.currentDate).add(1, 'years');
        if (dayjs(newDate).get('year') < this.MAX_YEAR) {
            this.currentDate = newDate;
            this.generateCalendar();
        }
    }

    isSelectedMonth(date: dayjs.Dayjs): boolean {
        return dayjs(date).isSame(this.currentDate, 'month');
    }

    selectDate(date: CalendarDate) {
        const selectedDate = dayjs(date.mDate).format('YYYY/MM/DD');
        this.selectedYear = selectedDate.substring(0, 4);
        this.selectedMonth = selectedDate.substring(5, 7);
        this.selectedDay = selectedDate.substring(8, 10);
        this.handleSelectDateEvent(selectedDate);
    }

    cancel() {
        this.selectedYear = '';
        this.selectedMonth = '';
        this.selectedDay = '';
        this.show = !this.show;
        this.handleSelectDateEvent('');
    }

    ok() {
        this.show = !this.show;
    }

    handleSelectDateEvent(selectedDate) {
        this.onSelectDateEvent.emit({
            value: selectedDate,
            name: this.name,
        });
    }

    onYearInputChange(event) {
        const newYear = event.target.value;
        const newMonth = this.selectedMonth ? this.selectedMonth : '01';
        const newDay = this.selectedDay ? this.selectedDay : '01';
        const date = newYear + '/' + newMonth.padStart(2, '0') + '/' + newDay.padStart(2, '0');
        if (!newYear) {
            this.invalidYearError = undefined;
            return;
        }
        if (newYear.length !== 4 || !Number(newYear)) {
            this.invalidYearError = 'errors.calendar.validation.invalid-year';
            return;
        } else if (!this.dateIsValid(newYear, newMonth, newDay)) {
            return;
        } else {
            this.invalidYearError = undefined;
            this.selectedYear = newYear;
            this.handleSelectDateEvent(date);
            this.currentDate = dayjs(this.currentDate).year(Number(newYear));
            this.generateCalendar();
        }
    }

    onMonthInputChange(event) {
        const newMonth = event.target.value;
        const newYear = this.selectedYear ? this.selectedYear : '1990';
        const newDay = this.selectedDay ? this.selectedDay : '01';
        const date = newYear + '/' + newMonth.padStart(2, '0') + '/' + newDay.padStart(2, '0');
        if (!newMonth) {
            this.invalidMonthError = undefined;
            return;
        }
        if (Number(newMonth) > 12 || !Number(newMonth)) {
            this.invalidMonthError = 'errors.calendar.validation.invalid-month';
            return;
        } else if (!this.dateIsValid(newYear, newMonth, newDay)) {
            return;
        } else {
            this.invalidMonthError = undefined;
            this.selectedMonth = newMonth;
            this.handleSelectDateEvent(date);
            this.currentDate = dayjs(this.currentDate).month(Number(newMonth) - 1);
            this.generateCalendar();
        }
    }

    onDayInputChange(event) {
        const newDay = event.target.value;
        const newYear = this.selectedYear ? this.selectedYear : '1990';
        const newMonth = this.selectedMonth ? this.selectedMonth : '01';
        const date = newYear + '/' + newMonth.padStart(2, '0') + '/' + newDay.padStart(2, '0');
        if (!newDay) {
            this.invalidDayError = undefined;
            return;
        }
        if (Number(newDay) > 31 || !Number(newDay)) {
            this.invalidDayError = 'errors.calendar.validation.invalid-dayjs';
            return;
        } else if (!this.dateIsValid(newYear, newMonth, newDay)) {
            return;
        } else {
            this.invalidDayError = undefined;
            this.selectedDay = newDay;
            this.handleSelectDateEvent(date);
            this.currentDate = dayjs(this.currentDate).date(Number(newDay));
            this.generateCalendar();
        }
    }

    dateIsValid(year, month, dayjs) {
        const date = new Date(Number(year), Number(month) - 1, Number(dayjs));
        if (year < this.MIN_YEAR) {
            this.invalidYearError = 'errors.validation.generic.below-minimum';
        } else if (year > this.MAX_YEAR) {
            this.invalidYearError = 'errors.validation.generic.above-maximum';
        } else if (
            date.getFullYear() === Number(year) &&
            date.getMonth() === Number(month) - 1 &&
            date.getDate() === Number(dayjs)
        ) {
            this.invalidDateError = undefined;
            return true;
        } else {
            this.invalidDateError = 'errors.calendar.validation.invalid-date';
            return false;
        }
    }

    toggleCalendar() {
        this.show = !this.show;
    }

    calendarHasErrors() {
        return (
            this.invalidYearError ||
            this.invalidMonthError ||
            this.invalidDayError ||
            this.invalidDateError
        );
    }

    getErrorMessage() {
        const error =
            this.invalidDateError ||
            this.invalidYearError ||
            this.invalidMonthError ||
            this.invalidDayError;
        return error;
    }
}

export interface CalendarDate {
    mDate: dayjs.Dayjs;
    selected?: boolean;
    today?: boolean;
}
